import React, { FC, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../http/index';

const TOKEN_KEY = 'superadmin_token';

interface Stats {
  studentCount: number;
  teacherCount: number;
}

const AdminPage: FC = () => {
  const [token, setToken] = useState<string | null>(sessionStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState('');
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) fetchStats(token);
  }, [token]);

  const fetchStats = async (t: string) => {
    try {
      const { data } = await axios.get<Stats>(`${API_URL}/superadmin/userstats`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setStats(data);
    } catch {
      sessionStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setError('Session expired. Please log in again.');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post<{ token: string }>(`${API_URL}/superadmin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch {
      setError('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setStats(null);
    setPassword('');
  };

  if (!token) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '5rem' }}>
        <h2 style={{ marginBottom: '0.5rem', color: '#4e418d' }}>Admin</h2>
        <p style={{ marginBottom: '2rem', color: '#888', fontSize: '0.9rem' }}>SUPERADMIN access only</p>
        <form
          onSubmit={handleLogin}
          style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '280px' }}
        >
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoFocus
            style={{
              padding: '0.75rem 1rem',
              fontSize: '1rem',
              borderRadius: '8px',
              border: '1px solid #ccc',
              outline: 'none',
            }}
          />
          {error && (
            <p style={{ color: '#e74c3c', fontSize: '0.9rem', margin: 0 }}>{error}</p>
          )}
          <button
            type="submit"
            disabled={loading || !password}
            style={{
              padding: '0.75rem',
              background: '#4e418d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: loading || !password ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              opacity: loading || !password ? 0.7 : 1,
            }}
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '5rem' }}>
      <h2 style={{ marginBottom: '2rem', color: '#4e418d' }}>SUPERADMIN Dashboard</h2>

      {stats ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            minWidth: '260px',
          }}
        >
          <div
            style={{
              background: '#f4f2fb',
              borderRadius: '12px',
              padding: '2rem',
              textAlign: 'center',
              border: '1px solid #e0dbf5',
            }}
          >
            <div style={{ fontSize: '3.5rem', fontWeight: 700, color: '#4e418d', lineHeight: 1 }}>
              {stats.studentCount}
            </div>
            <div style={{ marginTop: '0.5rem', color: '#555', fontSize: '1rem' }}>
              Registered Students
            </div>
          </div>

          <div
            style={{
              background: '#f4f2fb',
              borderRadius: '12px',
              padding: '2rem',
              textAlign: 'center',
              border: '1px solid #e0dbf5',
            }}
          >
            <div style={{ fontSize: '3.5rem', fontWeight: 700, color: '#4e418d', lineHeight: 1 }}>
              {stats.teacherCount}
            </div>
            <div style={{ marginTop: '0.5rem', color: '#555', fontSize: '1rem' }}>
              Registered Teachers
            </div>
          </div>

          <div
            style={{
              background: '#f4f2fb',
              borderRadius: '12px',
              padding: '2rem',
              textAlign: 'center',
              border: '1px solid #e0dbf5',
            }}
          >
            <div style={{ fontSize: '3.5rem', fontWeight: 700, color: '#4e418d', lineHeight: 1 }}>
              {stats.studentCount + stats.teacherCount}
            </div>
            <div style={{ marginTop: '0.5rem', color: '#555', fontSize: '1rem' }}>
              Total Users
            </div>
          </div>
        </div>
      ) : (
        <p style={{ color: '#888' }}>Loading stats...</p>
      )}

      <button
        onClick={() => stats ? fetchStats(token) : undefined}
        style={{
          marginTop: '1.5rem',
          padding: '0.6rem 1.5rem',
          background: 'transparent',
          color: '#4e418d',
          border: '1px solid #4e418d',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '0.9rem',
        }}
      >
        Refresh
      </button>

      <button
        onClick={handleLogout}
        style={{
          marginTop: '0.75rem',
          padding: '0.6rem 1.5rem',
          background: 'transparent',
          color: '#e74c3c',
          border: '1px solid #e74c3c',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '0.9rem',
        }}
      >
        Logout
      </button>
    </div>
  );
};

export default AdminPage;
